import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import {
  WhatsappLogo,
  CheckCircle,
  ArrowsClockwise,
  SignOut,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function WhatsAppPage() {
  const [status, setStatus] = useState({ connected: false, service_up: false, user: null });
  const [qr, setQr] = useState(null);
  const [loading, setLoading] = useState(false);

  const poll = async () => {
    try {
      const [s, q] = await Promise.all([
        api.get("/whatsapp/status"),
        api.get("/whatsapp/qr"),
      ]);
      setStatus(s.data);
      setQr(q.data?.qr || null);
    } catch (_) {}
  };

  useEffect(() => {
    poll();
    const t = setInterval(poll, 3000);
    return () => clearInterval(t);
  }, []);

  const logoutWA = async () => {
    if (!confirm("Desconectar o WhatsApp? Será necessário escanear o QR novamente.")) return;
    setLoading(true);
    try {
      await api.post("/whatsapp/logout");
      toast.success("Desconectando WhatsApp…");
      setTimeout(poll, 2000);
    } catch (e) {
      toast.error("Falha ao desconectar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout
      title="Conexão WhatsApp"
      subtitle="Bot / WhatsApp Web"
      action={
        <button
          data-testid="refresh-wa-btn"
          onClick={poll}
          className="flex items-center gap-2 border border-[#E5E7EB] px-4 py-2 text-sm hover:bg-[#F8F9FA] transition-colors duration-150"
        >
          <ArrowsClockwise size={16} weight="bold" /> Atualizar
        </button>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-[#E5E7EB] bg-white p-8 flex flex-col items-center justify-center min-h-[420px] tech-grid">
          {status.connected ? (
            <div data-testid="wa-connected" className="text-center">
              <CheckCircle size={64} weight="duotone" className="text-[#10B981] mx-auto" />
              <h2 className="font-display font-black text-3xl mt-4 tracking-tight">
                Conectado
              </h2>
              <p className="text-sm text-[#64748B] mt-2 mono">
                {status.user?.id || ""}
              </p>
              <button
                data-testid="wa-logout-btn"
                onClick={logoutWA}
                disabled={loading}
                className="mt-6 flex items-center gap-2 mx-auto border border-[#EF4444] text-[#EF4444] px-4 py-2 text-sm hover:bg-[#EF4444] hover:text-white transition-colors duration-150"
              >
                <SignOut size={16} weight="bold" /> Desconectar
              </button>
            </div>
          ) : qr ? (
            <div data-testid="wa-qr" className="text-center">
              <div className="bg-white p-4 inline-block border border-[#E5E7EB]">
                <img src={qr} alt="QR Code" width={280} height={280} />
              </div>
              <h2 className="font-display font-black text-xl mt-6 tracking-tight">
                Escaneie com seu WhatsApp
              </h2>
              <p className="text-xs text-[#64748B] mt-2 max-w-xs mx-auto leading-relaxed">
                Abra WhatsApp → Configurações → Dispositivos conectados → Conectar um dispositivo
              </p>
            </div>
          ) : (
            <div data-testid="wa-loading" className="text-center">
              <WhatsappLogo size={64} weight="duotone" className="text-[#64748B] mx-auto animate-pulse" />
              <h2 className="font-display font-black text-xl mt-4 tracking-tight">
                Iniciando serviço…
              </h2>
              <p className="text-xs text-[#64748B] mt-2 mono">
                {status.service_up ? "Aguardando QR Code" : "Conectando ao serviço Baileys"}
              </p>
            </div>
          )}
        </div>

        <div className="border border-[#E5E7EB] bg-white p-6">
          <div className="label-mono">Status</div>
          <h2 className="font-display font-bold text-xl mt-1 mb-5 tracking-tight">
            Estado do serviço
          </h2>
          <dl className="divide-y divide-[#E5E7EB] text-sm">
            <Row label="Serviço Baileys" value={status.service_up ? "Online" : "Offline"} ok={status.service_up} />
            <Row label="Conexão WhatsApp" value={status.connected ? "Conectado" : "Desconectado"} ok={status.connected} />
            <Row label="Número conectado" value={status.user?.id || "—"} />
            <Row label="Nome" value={status.user?.name || "—"} />
          </dl>

          <div className="mt-8">
            <div className="label-mono mb-2">Como funciona</div>
            <ol className="text-sm text-[#0F172A] space-y-2 list-decimal pl-5 leading-relaxed">
              <li>Escaneie o QR Code com o WhatsApp do número que vai atender clientes.</li>
              <li>Cliente envia uma mensagem com a palavra <span className="kbd">teste</span> + nome (ex: <span className="kbd">teste joao</span>).</li>
              <li>O bot cria um usuário de teste P2P no painel.fun e envia automaticamente para o cliente.</li>
              <li>Limite de 1 teste por número/IP a cada 24h (configurável).</li>
            </ol>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Row({ label, value, ok }) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="label-mono">{label}</div>
      <div className={`text-sm mono ${ok === undefined ? "text-[#0F172A]" : ok ? "text-[#10B981]" : "text-[#EF4444]"}`}>
        {value}
      </div>
    </div>
  );
}
